/**
 * What a machine's agent found on it (Machine.readiness, R-ON-4, ADR 0090 decision 4), in two
 * shapes: the drawer's rows on the Agents tab (ReadinessList), and the one line Add machine's
 * connected state and Getting started show, with an Add as project action for each repository
 * found there that nothing watches yet (ReadinessFound). Where a person must act on that
 * computer (a harness signed out, git without an identity), one sentence says so: Orgus
 * cannot sign in for them from here.
 */
import { CircleCheck, CircleHelp, CircleSlash, CircleX, Download, FolderGit2, FolderPlus, GitBranch, Loader, LogIn, ScanSearch, SquareTerminal, TriangleAlert } from '@orgus/ui/icons';
import type { LucideIcon } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { CODEX_WINDOWS_INSTALL_COMMAND, isInstallableAgentKind } from '@orgus/protocol';
import type { AgentInstallHubState, Machine } from '@orgus/protocol';
import { Link } from 'react-router';
import { Button, CopyButton, ErrorState, RelativeTime, cn, formatInt, shortPath, useNow } from '@orgus/ui';
import { runAction } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { AgentKindLabel } from '../adapters/AgentKind.tsx';
import { mockProjectAdded } from '../projects/mockEffects.ts';
import { useMe } from '../session-detail/api.ts';
import { startInstall, useAgentInstall } from './agentInstall.ts';
import { agentLines, lineSentence, linesToAct } from './install-model.ts';
import type { AgentLine, AgentLineState } from './install-model.ts';
import { foundLine, foundRepos, readinessAsks, readinessRows } from './readiness-model.ts';
import type { FoundRepo, ReadinessRow, ReadinessTone } from './readiness-model.ts';

const TONE: Record<ReadinessTone, { icon: LucideIcon; className: string }> = {
  ok: { icon: CircleCheck, className: 'text-fg-muted' },
  act: { icon: TriangleAlert, className: 'text-waiting' },
  off: { icon: CircleSlash, className: 'text-fg-subtle' },
  unknown: { icon: CircleHelp, className: 'text-fg-subtle' },
};

/** A report older agents never send: say so after a minute online, instead of looking forever. */
const NO_REPORT_AFTER_MS = 60_000;

/** The repositories a machine's report found, placed against what this console knows of it. */
function useFoundRepos(m: Machine): FoundRepo[] {
  const checkouts = useOrgus((s) => s.checkouts);
  const suggestions = useOrgus((s) => s.suggestions);
  return useMemo(() => (m.readiness ? foundRepos(m, m.readiness, Object.values(checkouts), Object.values(suggestions)) : []), [m, checkouts, suggestions]);
}

function Asks({ sentences }: { sentences: string[] }) {
  if (!sentences.length) return null;
  return (
    <>
      {sentences.map((s) => (
        <p key={s} className="flex items-start gap-2 text-ui text-fg-muted" data-testid="readiness-ask">
          <span className="flex h-5 shrink-0 items-center">
            <TriangleAlert className="size-4 text-waiting" aria-hidden />
          </span>
          <span className="min-w-0">{s}</span>
        </p>
      ))}
    </>
  );
}

function Row({ row }: { row: ReadinessRow }) {
  const { icon: Icon, className } = TONE[row.tone];
  const title = row.kind ? (
    <AgentKindLabel kind={row.kind} className="min-w-0 truncate text-ui text-fg" />
  ) : (
    <span className="flex min-w-0 items-center gap-2 truncate text-ui text-fg">
      {row.key === 'git' ? <GitBranch className="size-4 shrink-0 text-fg-subtle" aria-hidden /> : <FolderGit2 className="size-4 shrink-0 text-fg-subtle" aria-hidden />}
      <span className="truncate">{row.title}</span>
    </span>
  );
  return (
    <li data-row="" data-readiness={row.key} data-tone={row.tone} className="flex h-row items-center gap-2 border-b border-border-subtle px-3 last:border-b-0">
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {title}
        {row.meta && (
          <span data-meta="" className="shrink-0 text-caption text-fg-subtle num">
            {row.meta}
          </span>
        )}
      </span>
      <span className={cn('inline-flex shrink-0 items-center gap-1 text-caption', className)}>
        <Icon className="size-3.5" aria-hidden />
        {row.status}
      </span>
    </li>
  );
}

const LINE: Record<AgentLineState, { icon: LucideIcon; className: string; spin?: boolean }> = {
  ready: { icon: CircleCheck, className: 'text-fg-muted' },
  installed: { icon: CircleCheck, className: 'text-fg-muted' },
  unknown: { icon: CircleHelp, className: 'text-fg-subtle' },
  'sign-in': { icon: TriangleAlert, className: 'text-waiting' },
  installing: { icon: Loader, className: 'text-working', spin: true },
  failed: { icon: CircleX, className: 'text-failed' },
  'not-installed': { icon: CircleSlash, className: 'text-fg-subtle' },
  'needs-node': { icon: CircleSlash, className: 'text-fg-subtle' },
  'in-terminal': { icon: SquareTerminal, className: 'text-fg-subtle' },
  unavailable: { icon: CircleSlash, className: 'text-fg-subtle' },
};

/**
 * Install on the agent lines (R-ON-9, ADR 0099): who may press it (not a viewer; the hub checks
 * again: an owner, admin or the person whose computer it is), the one in flight, and the error.
 */
function useInstall(m: Machine) {
  const me = useMe();
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<unknown>(null);
  const may = m.online && me.role !== 'viewer';
  const install = async (l: AgentLine) => {
    if (!isInstallableAgentKind(l.kind)) return;
    setBusy(l.kind);
    setFailed(null);
    try {
      await startInstall(m.id, l.kind);
    } catch (e) {
      setFailed(e);
    } finally {
      setBusy(null);
    }
  };
  return { may, busy, failed, install };
}

function AgentRow({ line: l, may, busy, onInstall }: { line: AgentLine; may: boolean; busy: string | null; onInstall: (l: AgentLine) => void }) {
  const { icon: Icon, className, spin } = LINE[l.state];
  const offer = may && l.installable;
  return (
    <li data-row="" data-agent-line={l.kind} data-state={l.state} className="flex h-row items-center gap-2 border-b border-border-subtle px-3 last:border-b-0">
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <AgentKindLabel kind={l.kind} className="min-w-0 truncate text-ui text-fg" />
        {l.meta && (
          <span data-meta="" className="shrink-0 text-caption text-fg-subtle num">
            {l.meta}
          </span>
        )}
      </span>
      <span className={cn('inline-flex shrink-0 items-center gap-1 text-caption', className)} role={l.state === 'installing' ? 'status' : undefined}>
        <Icon className={cn('size-3.5', spin && 'motion-safe:animate-spin')} aria-hidden />
        {l.label}
      </span>
      {offer && (
        <Button size="sm" variant="secondary" icon={<Download />} loading={busy === l.kind} disabled={busy != null && busy !== l.kind} onClick={() => onInstall(l)}>
          {l.state === 'failed' ? 'Install again' : 'Install'}
        </Button>
      )}
    </li>
  );
}

/** The sentences under the agent lines: why an install failed, how to sign in, what Gemini CLI needs, the command to run in a terminal. */
function LineNotes({ lines, machineName }: { lines: AgentLine[]; machineName: string }) {
  const notes = lines.flatMap((l) => {
    const text = lineSentence(l, machineName);
    return text ? [{ l, text }] : [];
  });
  if (!notes.length) return null;
  return (
    <>
      {notes.map(({ l, text }) => (
        <p key={l.kind} className="flex items-start gap-2 text-ui text-fg-muted" data-testid="agent-line-note" data-state={l.state}>
          <span className="flex h-5 shrink-0 items-center">
            {l.state === 'failed' ? (
              <CircleX className="size-4 text-failed" aria-hidden />
            ) : l.state === 'sign-in' ? (
              <LogIn className="size-4 text-waiting" aria-hidden />
            ) : l.state === 'in-terminal' ? (
              <SquareTerminal className="size-4 text-fg-subtle" aria-hidden />
            ) : (
              <TriangleAlert className="size-4 text-fg-subtle" aria-hidden />
            )}
          </span>
          <span className="min-w-0">
            {text}
            {l.state === 'in-terminal' && (
              <>
                {' '}
                <CopyButton text={CODEX_WINDOWS_INSTALL_COMMAND} label="Copy the install command" className="align-middle" />
              </>
            )}
            {l.state === 'sign-in' && l.kind === 'claude-code' && (
              <>
                {' '}
                <Link to="/settings/accounts" className="text-accent-text underline-offset-2 hover:underline">
                  Open accounts
                </Link>
              </>
            )}
          </span>
        </p>
      ))}
    </>
  );
}

/**
 * One line per agent with Install where Orgus can install it, then `rows` (git, repositories) in
 * the same list, the notes under them and `children` (more sentences).
 */
export function AgentLines({ machine: m, lines, state, rows, children }: { machine: Machine; lines: AgentLine[]; state: AgentInstallHubState | null; rows?: ReactNode; children?: ReactNode }) {
  const { may, busy, failed, install } = useInstall(m);
  if (!lines.length && !rows) return <>{children}</>;
  return (
    <div className="flex flex-col gap-2" data-testid="agent-lines">
      <ul className="overflow-hidden rounded-card border border-border" aria-label="Coding agents and tools">
        {lines.map((l) => (
          <AgentRow key={l.kind} line={l} may={may && !!state} busy={busy} onInstall={(x) => void install(x)} />
        ))}
        {rows}
      </ul>
      <LineNotes lines={lines} machineName={m.name} />
      {children}
      {failed != null && <ErrorState variant="inline" error={failed} />}
    </div>
  );
}

/** The drawer's Agents tab: each harness with its sign-in state, git, and the repositories found. */
export function ReadinessList({ machine: m }: { machine: Machine }) {
  const repos = useFoundRepos(m);
  const install = useAgentInstall();
  const lines = agentLines(m, install);
  const r = m.readiness;
  return (
    <section aria-label="Found on this machine" className="flex flex-col gap-2">
      <h3 className="flex h-control-sm items-center gap-2 text-caption font-medium text-fg-muted">
        Found on this machine
        <span className="font-normal text-fg-subtle">{r ? <RelativeTime ts={r.checkedAt} prefix="Checked" /> : m.online ? 'Not reported yet: update its Orgus agent' : 'Reported when it is online'}</span>
      </h3>
      <AgentLines
        machine={m}
        lines={lines}
        state={install}
        rows={
          r
            ? readinessRows(r, repos.filter((x) => x.watched).length)
                .filter((row) => !row.kind)
                .map((row) => <Row key={row.key} row={row} />)
            : undefined
        }
      >
        {r && <Asks sentences={readinessAsks(r, m.name).filter((x) => !x.includes(' signed out on '))} />}
      </AgentLines>
    </section>
  );
}

/** Repositories offered as projects in the found line, before the rest wait for the next step. */
const REPOS_SHOWN = 3;

/**
 * The one line, "Found on this computer: Claude Code (signed in), git, 3 repositories", the
 * sentence where a person must act there, and Add as project for each repository found that
 * nothing watches yet. Adding one calls project.add on that machine and then `onAdded`.
 */
export function ReadinessFound({ machine: m, where, onAdded }: { machine: Machine; where: string; onAdded?: () => void }) {
  const now = useNow(1000);
  const repos = useFoundRepos(m);
  const install = useAgentInstall();
  const toAct = linesToAct(agentLines(m, install));
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<unknown>(null);
  const r = m.readiness;
  if (!r) {
    if (!m.online) return null;
    const late = m.connectedAt != null && now - m.connectedAt > NO_REPORT_AFTER_MS;
    return (
      <p className="flex items-center gap-2 text-ui text-fg-subtle" role="status" data-testid="readiness-pending">
        <ScanSearch className="size-4 shrink-0" aria-hidden />
        {late ? `${m.name} has not said what is on it: update its Orgus agent there.` : `Looking at what is on ${where}`}
      </p>
    );
  }
  const addable = repos.filter((x) => !x.watched && x.path);
  const shown = addable.slice(0, REPOS_SHOWN);
  const add = async (x: FoundRepo) => {
    if (!x.path) return;
    setBusy(x.rel);
    setFailed(null);
    try {
      await runAction(m.id, 'project.add', { machineId: m.id, path: x.path });
      mockProjectAdded(m.id, x.path);
      onAdded?.();
    } catch (e) {
      setFailed(e);
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="flex flex-col gap-2" data-testid="readiness-found">
      <p className="flex items-start gap-2 text-ui text-fg">
        <span className="flex h-5 shrink-0 items-center">
          <ScanSearch className="size-4 text-fg-subtle" aria-hidden />
        </span>
        <span className="min-w-0">{foundLine(r, where)}</span>
      </p>
      <AgentLines machine={m} lines={toAct} state={install} />
      <Asks sentences={readinessAsks(r, m.name).filter((x) => !x.includes(' signed out on '))} />
      {shown.length > 0 && (
        <ul className="overflow-hidden rounded-card border border-border" aria-label="Repositories found that nothing watches yet">
          {shown.map((x) => (
            <li key={x.rel} data-row="" className="flex h-row items-center gap-3 border-b border-border-subtle px-3 last:border-b-0">
              <FolderGit2 className="size-4 shrink-0 text-fg-subtle" aria-hidden />
              <span className="flex min-w-0 flex-1 items-baseline gap-3">
                <span className="truncate text-ui font-medium text-fg">{x.name}</span>
                <span data-meta="" className="hidden min-w-0 truncate font-mono text-caption text-fg-subtle sm:inline">
                  {shortPath(`~/${x.rel}`, 48)}
                </span>
              </span>
              <Button size="sm" variant="secondary" icon={<FolderPlus />} loading={busy === x.rel} disabled={busy != null && busy !== x.rel} onClick={() => add(x)}>
                Add as project
              </Button>
            </li>
          ))}
        </ul>
      )}
      {addable.length > shown.length && <p className="text-caption text-fg-subtle">{formatInt(addable.length - shown.length)} more on the next step</p>}
      {failed != null && <ErrorState variant="inline" error={failed} />}
    </div>
  );
}
